export const PercentColours = [
    '#FF0303',
    '#FF0403',
    '#FF0603',
    '#FF0903',
    '#FF0C03',
    '#FF0F03',
    '#FF1203',
    '#FF1503',
    '#FF1903',
    '#FF1C03',
    '#FF1F03',
    '#FF2203',
    '#FF2503',
    '#FF2803',
    '#FF2B03',
    '#FF2F03',
    '#FF3203',
    '#FF3503',
    '#FF3803',
    '#FF3B03',
    '#FF3E03',
    '#FF4103',
    '#FF4503',
    '#FF4803',
    '#FF4B03',
    '#FF4E03',
    '#FF5103',
    '#FF5403',
    '#FF5703',
    '#FF5A03',
    '#FF5D03',
    '#FF6003',
    '#FF6303',
    '#FF6603',
    '#FF6903',
    '#FF6C03',
    '#FF6F03',
    '#FF7203',
    '#FF7503',
    '#FF7803',
    '#FF7B03',
    '#FF7E03',
    '#FF8103',
    '#FF8403',
    '#FF8703',
    '#FF8A03',
    '#FF8D03',
    '#FF9003',
    '#FA8F03',
    '#F58F03',
    '#F08F03',
    '#EB8E03',
    '#E68E03',
    '#E18E03',
    '#DC8D03',
    '#D88D03',
    '#D38D03',
    '#CE8C03',
    '#C98C03',
    '#C48C03',
    '#BF8C03',
    '#BA8B03',
    '#B68B03',
    '#B18B03',
    '#AC8A03',
    '#A78A03',
    '#A28A03',
    '#9D8903',
    '#988903',
    '#948903',
    '#8F8903',
    '#8A8803',
    '#858803',
    '#808803',
    '#7B8703',
    '#778703',
    '#728703',
    '#6D8603',
    '#698603',
    '#648603',
    '#5F8603',
    '#5A8503',
    '#568503',
    '#518503',
    '#4C8403',
    '#488403',
    '#438403',
    '#3E8303',
    '#3A8303',
    '#358303',
    '#308303',
    '#2B8203',
    '#278203',
    '#228203',
    '#1D8103',
    '#198103',
    '#148103',
    '#0F8003',
    '#0A8003',
    '#068003',
    '#038003',
    '#038103',
    '#038403',
    '#038703',
    '#038A03',
    '#038C03',
    '#038F03',
    '#039203',
    '#039503',
    '#039803',
    '#039A03',
    '#039D03',
    '#03A003',
    '#03A303',
    '#03A503',
    '#03A803',
    '#03AB03',
    '#03AE03',
    '#03B003',
    '#03B303',
    '#03B603',
    '#03B903',
    '#03BC03',
    '#03BE03',
    '#03C003',
    '#03C303',
    '#03C503',
    '#03C803',
    '#03CA03',
    '#03CD03',
    '#03CF03',
    '#03D203',
    '#03D403',
    '#03D703',
    '#03D903',
    '#03DC03',
    '#03DE03',
    '#03E103',
    '#03E303',
    '#03E603',
    '#03E803',
    '#03EB03',
    '#03ED03',
    '#03F003',
    '#03F203',
    '#03F503',
    '#03F703',
    '#03FA03',
    '#03FD03',
    '#03FF03'
  ]

// ──────────────────────────────────────────────────────────────────────────────
// User-customisable grading colours.
// Stored as 11 stops (0%, 10%, …, 100%) and interpolated to a 150-entry array
// for the heatmap/zone displays. Persisted in localStorage.
// ──────────────────────────────────────────────────────────────────────────────
const GRADING_STOPS_KEY = "psts_grading_stops";

// Default stops sampled from the original PercentColours gradient
// (indices 0, 15, 30, …, 135, 149) so existing visuals are preserved.
export const DEFAULT_GRADING_STOPS = (() => {
  const out = [];
  for (let i = 0; i < 11; i++) {
    const idx = Math.min(149, Math.round((i / 10) * 149));
    out.push(PercentColours[idx]);
  }
  return out;
})();

function clampHex(c) {
  const n = Math.max(0, Math.min(255, Math.round(c)));
  return n.toString(16).padStart(2, "0");
}
function parseHex(h) {
  const m = /^#?([0-9a-f]{6})$/i.exec(h ?? "");
  if (!m) return null;
  const v = parseInt(m[1], 16);
  return { r: (v >> 16) & 0xff, g: (v >> 8) & 0xff, b: v & 0xff };
}
function toHex({ r, g, b }) {
  return `#${clampHex(r)}${clampHex(g)}${clampHex(b)}`.toUpperCase();
}

export function loadGradingStops() {
  try {
    const raw = localStorage.getItem(GRADING_STOPS_KEY);
    if (!raw) return [...DEFAULT_GRADING_STOPS];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length === 11 && parsed.every((c) => parseHex(c))) {
      return parsed.map((c) => c.toUpperCase());
    }
  } catch {
    /* fall through */
  }
  return [...DEFAULT_GRADING_STOPS];
}

export function saveGradingStops(stops) {
  try {
    localStorage.setItem(GRADING_STOPS_KEY, JSON.stringify(stops));
  } catch {
    /* ignore quota errors */
  }
}

export function buildPercentColoursFromStops(stops) {
  const rgbStops = stops.map(parseHex).filter(Boolean);
  if (rgbStops.length < 2) return [...PercentColours];
  const out = new Array(150);
  const segCount = rgbStops.length - 1;
  for (let i = 0; i < 150; i++) {
    const t = (i / 149) * segCount; // 0..segCount
    const seg = Math.min(segCount - 1, Math.floor(t));
    const f = t - seg;
    const a = rgbStops[seg];
    const b = rgbStops[seg + 1];
    out[i] = toHex({
      r: a.r + (b.r - a.r) * f,
      g: a.g + (b.g - a.g) * f,
      b: a.b + (b.b - a.b) * f,
    });
  }
  return out;
}

// Cached active palette + change subscribers.
let activePercentColours = buildPercentColoursFromStops(loadGradingStops());
const subscribers = new Set();

export function getActivePercentColours() {
  return activePercentColours;
}

export function setGradingStops(stops) {
  saveGradingStops(stops);
  activePercentColours = buildPercentColoursFromStops(stops);
  subscribers.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore */
    }
  });
}

export function subscribeGradingChange(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

export function randomGradingStops() {
  const out = [];
  for (let i = 0; i < 11; i++) {
    const r = Math.floor(Math.random() * 256);
    const g = Math.floor(Math.random() * 256);
    const b = Math.floor(Math.random() * 256);
    out.push(toHex({ r, g, b }));
  }
  return out;
}
